import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddRounded from '@mui/icons-material/AddRounded';
import {
  EmptyState, ListTable, Panel, StatusChip, ToolbarButton, type ListColumn, type StatusTone,
} from '../../../ui-kit';
import { fetchIntegrations, type Integration } from '../../../services/roccoService';
import ConnectionModal from './ConnectionModal';
import IntegrationPicker, { DIALER_PARTS, INTEGRATION_CHOICES, type IntegrationChoice } from './IntegrationPicker';
import { fromNow, useLive } from './live';

const whole = (v: number) => v.toLocaleString();

/** Connected, needs attention, or not joined at all — from the live marks. */
function stateOf(row: Integration): { tone: StatusTone; label: string } {
  if (!row.connected) return { tone: 'neutral', label: 'Not connected' };
  if (row.error || row.job?.last_error) return { tone: 'warning', label: 'Needs attention' };
  return { tone: 'good', label: 'Connected' };
}

/** What it has done today, in a line. */
function activityOf(row: Integration): string {
  const job = row.job;
  if (!row.connected) return 'Not connected';
  if (row.id === 'hubspot') {
    return job ? `Checked ${fromNow(job.last_ok_at)} · ${whole(job.handled_today)} new today` : 'Not checked yet';
  }
  if (row.id === 'warehouse') {
    return job ? `Read ${fromNow(job.last_ok_at)} · ${whole(job.handled_today)} taken into the queue today` : 'Not read yet';
  }
  return `${whole(row.calls_today ?? 0)} calls today · ${whole(row.connected_today ?? 0)} contacted`;
}

/**
 * What ROCCO is joined to, in and out, as it is right now — and what else
 * plugs into the same places.
 *
 * The connected rows are read from the running system: each says when it
 * last worked and what came through it today. Below them, the adaptors: other
 * CRMs, dialers and warehouses that take the same place as the ones in use.
 */
export default function IntegrationsPage() {
  const navigate = useNavigate();
  const { data, error, loading } = useLive(fetchIntegrations);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [picking, setPicking] = React.useState(false);

  const live = data?.integrations ?? [];
  const connectedIds = live.filter((row) => row.connected).map((row) => row.id as string);
  const adaptors = INTEGRATION_CHOICES.filter((choice) => !connectedIds.includes(choice.id));
  const logoOf = (id: string) => INTEGRATION_CHOICES.find((choice) => choice.id === id)?.logo;

  const openLive = live.find((row) => row.id === openId);
  const openAdaptor = openLive ? null : INTEGRATION_CHOICES.find((choice) => choice.id === openId);

  const liveColumns: ListColumn<Integration>[] = [
    {
      key: 'name',
      header: 'System',
      grow: 1.3,
      minWidth: 190,
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>{logoOf(row.id)}</Box>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
            {row.built_on ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {row.built_on.map((part, i) => (
                  <React.Fragment key={part}>
                    {i > 0 ? <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>+</Typography> : null}
                    <Box sx={{ display: 'flex' }}>{DIALER_PARTS[part]}</Box>
                    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{part}</Typography>
                  </React.Fragment>
                ))}
              </Stack>
            ) : row.kind ? (
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{row.kind}</Typography>
            ) : null}
          </Stack>
        </Stack>
      ),
    },
    { key: 'role', header: 'Direction', width: 110, render: (row) => <Muted>{row.role}</Muted> },
    { key: 'state', header: 'State', width: 150, render: (row) => <StatusChip {...stateOf(row)} /> },
    { key: 'activity', header: 'Today', grow: 2, minWidth: 220, render: (row) => <Muted>{activityOf(row)}</Muted> },
    {
      key: 'open',
      header: '',
      width: 100,
      align: 'right',
      render: (row) => <ToolbarButton variant="ghost" onClick={() => setOpenId(row.id)}>Details</ToolbarButton>,
    },
  ];

  const adaptorColumns: ListColumn<IntegrationChoice>[] = [
    {
      key: 'name',
      header: 'System',
      grow: 1.3,
      minWidth: 190,
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>{row.logo}</Box>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
        </Stack>
      ),
    },
    { key: 'role', header: 'Direction', width: 110, render: (row) => <Muted>{row.group}</Muted> },
    { key: 'state', header: 'State', width: 150, render: () => <StatusChip tone="neutral" label="Available" /> },
    { key: 'purpose', header: 'What it does', grow: 2, minWidth: 220, render: (row) => <Muted>{row.purpose}</Muted> },
    {
      key: 'open',
      header: '',
      width: 100,
      align: 'right',
      render: (row) => <ToolbarButton variant="ghost" onClick={() => setOpenId(row.id)}>Details</ToolbarButton>,
    },
  ];

  return (
    <Stack spacing={2}>
      <Panel
        title="Connected"
        subtitle="What ROCCO reads from and calls through · live"
        actions={
          <ToolbarButton variant="brand" icon={<AddRounded fontSize="small" />} onClick={() => setPicking(true)}>
            New integration
          </ToolbarButton>
        }
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : (
          <ListTable columns={liveColumns} rows={live} getRowKey={(row) => row.id} loading={loading} loadingRows={3}
            onRowClick={(row) => setOpenId(row.id)} />
        )}
      </Panel>

      <Panel title="Adaptors" subtitle="Other systems that plug into the same places">
        <ListTable
          columns={adaptorColumns}
          rows={adaptors}
          getRowKey={(row) => row.id}
          onRowClick={(row) => setOpenId(row.id)}
          pageSize={10}
        />
      </Panel>

      <IntegrationPicker
        open={picking}
        onClose={() => setPicking(false)}
        onPick={(id) => setOpenId(id)}
        connectedIds={connectedIds}
      />

      {openLive ? (
        <ConnectionModal
          open
          onClose={() => setOpenId(null)}
          name={openLive.name}
          purpose={openLive.built_on ? `${openLive.role} · ${openLive.built_on.join(' + ')}` : openLive.role}
          state={stateOf(openLive)}
          problem={openLive.error || openLive.job?.last_error}
          facts={openLive.facts}
          activity={[
            { label: 'Last worked', value: openLive.job?.last_ok_at ? fromNow(openLive.job.last_ok_at) : '—' },
            ...(openLive.id === 'dialer'
              ? [
                  { label: 'Calls today', value: whole(openLive.calls_today ?? 0) },
                  { label: 'Contacted today', value: whole(openLive.connected_today ?? 0) },
                ]
              : [{ label: openLive.id === 'hubspot' ? 'New today' : 'Taken today', value: whole(openLive.job?.handled_today ?? 0) }]),
            ...(openLive.since ? [{ label: 'Connected since', value: new Date(openLive.since).toLocaleDateString() }] : []),
          ]}
          action={openLive.connector_id
            ? { label: 'Manage in Connectors', onClick: () => navigate(`/connectors/${openLive.connector_id}`) }
            : undefined}
        />
      ) : null}

      {openAdaptor ? (
        <ConnectionModal
          open
          onClose={() => setOpenId(null)}
          name={openAdaptor.name}
          purpose={`${openAdaptor.group} · ${openAdaptor.purpose}`}
          state={{ tone: 'neutral', label: 'Available' }}
          facts={[{ label: 'Plugs in', value: openAdaptor.plugsIn }]}
        />
      ) : null}
    </Stack>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>{children}</Typography>
  );
}
